import {
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
} from '@databases/types/database';
import { Database } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { convertType } from '@databases/utils/database/field-types';
import {
  getContentLabels,
  getReferencedLabelContentRefs,
} from '@databases/utils/database/field-types/pageLabels';
import { getContentStatus } from '@databases/utils/database/field-types/pageStatus';
import { createEntry, getDatabaseValue } from '@databases/utils/database/utils';
import { getOrApplyValueChange } from '@databases/utils/deltas/paste-value-delta';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { addChangeToDraft } from '../changesets/actions';
import { DefaultRootState } from '../redux';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

const getSourceValueText = (
  database: Database,
  sourceField: DatabaseField,
  sourceEntry: DatabaseEntry,
  sourceValue: DatabaseValue,
  getState: () => DefaultRootState
): string => {
  const sourceContentRefs = getReferencedLabelContentRefs(
    { database, field: sourceField, entry: sourceEntry },
    getState()
  );
  const convertedSourceValue = convertType(
    {
      database,
      field: sourceField,
      value: sourceValue,
    },
    DatabaseFieldType.TEXT,
    getState()
  );

  switch (sourceField.type) {
    case DatabaseFieldType.PAGE_LABELS: {
      return sourceContentRefs
        .map((contentRef) => getContentLabels(contentRef, {}, getState())?.join('\n'))
        .join('\n');
    }
    case DatabaseFieldType.PAGE_STATUS: {
      return sourceContentRefs
        .map((contentRef) => getContentStatus(contentRef, {}, getState())?.name ?? '')
        .join('\n');
    }
    default:
      return convertedSourceValue.value;
  }
};

export const doFillValues =
  (
    contentId: string,
    scope: string,
    selectedFieldIds: string[],
    selectedEntryIds: string[],
    sourceEntryIds: string[],
    sourceFieldIds: string[],
    newValue?: DatabaseValue
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const targetEntryIds = selectedEntryIds.filter(
      (entryId) => !sourceEntryIds.includes(entryId)
    );
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        let recurringEntryIdx = 0;
        targetEntryIds.forEach((entryId) => {
          selectedFieldIds.forEach((fieldId, fieldIdx) => {
            const sourceEntry = database.entriesById[sourceEntryIds[recurringEntryIdx]];
            const sourceField = database.fieldsById[sourceFieldIds[fieldIdx]];
            const sourceValue =
              newValue || getDatabaseValue(database, sourceField, sourceEntry);

            const targetEntry = database.entriesById[entryId];
            const targetField = database.fieldsById[fieldId];

            const text = getSourceValueText(
              database,
              sourceField,
              sourceEntry,
              sourceValue,
              getState
            );

            const changes = getOrApplyValueChange(
              {
                value: sourceValue,
                field: targetField,
                entry: targetEntry,
                database,
                text,
              },
              { dispatch, getState },
              false
            );
            changes?.forEach((change) => {
              dispatch(
                addChangeToDraft(contentId, scope, {
                  type: DatabaseChangeType.SET_VALUE,
                  fieldId: fieldId,
                  entryId: entryId,
                  value: sourceValue,
                  change: change,
                })
              );
            });
          });
          recurringEntryIdx++;
          if (recurringEntryIdx >= sourceEntryIds.length - 1) {
            recurringEntryIdx = 0;
          }
        });
      });
    });
  };

export const doAddEntry =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<DatabaseEntry> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const entry = createEntry(database, viewSettings);
    dispatch(
      addChangeToDraft(contentId, scope, { type: DatabaseChangeType.ADD_ENTRY, entry })
    );
    return entry;
  };
